import React, { memo, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getPlaceholderWebP, getProductPlaceholder } from "../lib/productPlaceholder";
import { trackEvent } from "../lib/analytics";
import { getCaseQuantity } from "../../../shared/caseQuantity";

export interface ProductListCardProps {
  p: any;
  isFavorite: boolean;
  customerPrice: string | undefined;
  canAddToCart: boolean;
  hasUser: boolean;
  userIsActive: boolean;
  rawInput: string;
  addingToCart: boolean;
  addedToCart: boolean;
  requestingProduct: boolean;
  productRequested: boolean;
  onToggleFavorite: (id: number) => void;
  onAddToCart: (e: React.MouseEvent, id: number) => void;
  onStepQty: (id: number, delta: number) => void;
  onSetRaw: (id: number, val: string) => void;
  onCommitRaw: (id: number) => void;
  onRequestSpecialOrder: (e: React.MouseEvent, id: number) => void;
  gridCols: string;
}

function ProductListCardInner({
  p,
  isFavorite,
  customerPrice,
  canAddToCart,
  hasUser,
  userIsActive,
  rawInput,
  addingToCart,
  addedToCart,
  requestingProduct,
  productRequested,
  onToggleFavorite,
  onAddToCart,
  onStepQty,
  onSetRaw,
  onCommitRaw,
  onRequestSpecialOrder,
  gridCols,
}: ProductListCardProps) {
  const [imageFailed, setImageFailed] = useState(false);
  const placeholder = getProductPlaceholder(p.id, p.dosageForm, p.route);
  const caseQuantity = getCaseQuantity(p.ndc);

  useEffect(() => {
    setImageFailed(false);
  }, [p.id, p.imageUrl]);

  return (
    <div className={`grid grid-cols-1 items-center ${gridCols} gap-2 md:gap-3 px-4 py-3 hover:bg-blue-50/40 transition-colors group`}>
      {hasUser && (
        userIsActive ? (
          <button
            onClick={() => onToggleFavorite(p.id)}
            aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
            className="hidden md:flex items-center justify-center w-7 h-7 rounded-full hover:bg-red-50 transition-colors"
          >
            <svg className={`w-4 h-4 transition-colors ${isFavorite ? "text-red-500" : "text-gray-300 hover:text-red-300"}`} viewBox="0 0 24 24" fill={isFavorite ? "currentColor" : "none"} stroke="currentColor" strokeWidth={2}>
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          </button>
        ) : (
          <span className="hidden md:block" />
        )
      )}

      <Link to={`/products/${p.id}`} className="contents">
        <div className="hidden md:flex w-10 h-10 rounded bg-gray-50 border border-gray-100 items-center justify-center flex-shrink-0 overflow-hidden">
          {p.imageUrl && !imageFailed ? (
            <img
              src={p.imageUrl}
              alt={[p.name, p.strength, p.dosageForm].filter(Boolean).join(" ")}
              className="w-full h-full object-contain"
              loading="lazy"
              onError={() => setImageFailed(true)}
            />
          ) : (
            <picture className="block w-full h-full">
              <source type="image/webp" srcSet={getPlaceholderWebP(placeholder)} />
              <img
                src={placeholder}
                alt={[p.name, p.strength, p.dosageForm].filter(Boolean).join(" ")}
                className="block w-full h-full object-contain"
                loading="lazy"
              />
            </picture>
          )}
        </div>

        <div className="min-w-0">
          <h3 className="text-sm font-medium text-primary group-hover:text-primary/80 truncate leading-tight">{p.name}</h3>
          <div className="md:hidden text-xs text-gray-500 mt-0.5">
            NDC: {p.ndc} &middot; {(p.manufacturer && p.manufacturer !== "Unknown" ? p.manufacturer : null) || p.labelerName || "—"}
          </div>
          {caseQuantity && (
            <div className="text-[10px] font-semibold text-amber-700 mt-0.5">
              Sold by case only · {caseQuantity} units/case
            </div>
          )}
          {p.dosageForm && <span className="hidden md:inline text-xs text-gray-600">{p.dosageForm}{p.route ? ` · ${p.route}` : ""}</span>}
        </div>

        <span className="hidden md:block text-xs text-gray-600 font-mono">{p.ndc}</span>
        <span className="hidden md:block text-xs text-gray-600">{p.strength || "—"}{p.packageSize ? ` / ${p.packageSize}` : ""}</span>
        <span className="hidden md:block text-xs text-gray-600 truncate">{(p.manufacturer && p.manufacturer !== "Unknown" ? p.manufacturer : null) || p.labelerName || "—"}</span>

        <div className="hidden md:flex items-center">
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${p.inStock ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-600"}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${p.inStock ? "bg-green-500" : "bg-blue-400"}`}></span>
            {p.inStock ? "In Stock" : "Request Item"}
          </span>
        </div>
      </Link>

      <div className="hidden md:block text-right">
        {customerPrice !== undefined ? (
          <span className="text-sm font-semibold text-green-600">${customerPrice}</span>
        ) : p.price && parseFloat(p.price) > 0 ? (
          <span className="text-sm font-semibold text-accent">${p.price}</span>
        ) : p.price !== null && p.price !== undefined ? (
          <span className="text-xs text-amber-600 font-medium">Contact for pricing</span>
        ) : hasUser && userIsActive ? (
          <span className="text-xs text-amber-600 font-medium">Contact for pricing</span>
        ) : (
          <Link
            to="/register"
            onClick={() => trackEvent("signup_intent", {
              location: "catalog_price_desktop",
              product_id: p.id,
            })}
            className="text-xs text-accent hover:underline"
          >
            Sign up
          </Link>
        )}
      </div>

      {canAddToCart && (
        <div className="hidden md:flex flex-col items-center justify-center gap-1">
          {p.inStock ? (
            <>
              <div className="flex items-center gap-1">
                <button aria-label={`Decrease quantity for ${p.name}`} onClick={e => { e.preventDefault(); e.stopPropagation(); onStepQty(p.id, -1); }} className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm font-bold leading-none">−</button>
                <input
                  type="number"
                  aria-label={`${caseQuantity ? "Cases" : "Quantity"} for ${p.name}`}
                  min={1}
                  value={rawInput}
                  onChange={e => { onSetRaw(p.id, e.target.value); }}
                  onBlur={() => onCommitRaw(p.id)}
                  onFocus={e => e.target.select()}
                  onClick={e => e.stopPropagation()}
                  className="w-10 text-center text-xs border border-gray-300 rounded py-0.5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button aria-label={`Increase quantity for ${p.name}`} onClick={e => { e.preventDefault(); e.stopPropagation(); onStepQty(p.id, +1); }} className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm font-bold leading-none">+</button>
              </div>
              {caseQuantity && <span className="text-[9px] text-amber-700">{caseQuantity} units per case</span>}
              <button
                onClick={(e) => onAddToCart(e, p.id)}
                disabled={addingToCart}
                aria-label={addedToCart ? `Added ${p.name} to cart` : `Add ${p.name} to cart`}
                className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-all duration-200 ${
                  addedToCart
                    ? "bg-green-50 text-green-700 border border-green-200"
                    : "bg-accent text-white hover:bg-accent/90 shadow-sm"
                }`}
              >
                {addingToCart ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : addedToCart ? "Added!" : "+ Cart"}
              </button>
            </>
          ) : (
            <div className="flex flex-col items-center gap-0.5">
              <button
                onClick={(e) => onRequestSpecialOrder(e, p.id)}
                disabled={requestingProduct || productRequested}
                aria-label={productRequested ? `${p.name} request submitted` : `Request 2-day shipping for ${p.name}`}
                className={`text-[10px] font-medium px-2 py-1 rounded-lg transition-all duration-200 whitespace-nowrap ${
                  productRequested
                    ? "bg-green-50 text-green-700 border border-green-200"
                    : "bg-orange-500 text-white hover:bg-orange-600 shadow-sm"
                }`}
                title="Availability may vary"
              >
                {requestingProduct ? "..." : productRequested ? "Requested!" : "Request Item"}
              </button>
              <span className="text-[9px] text-gray-400 leading-tight">Availability may vary</span>
            </div>
          )}
        </div>
      )}

      <div className="md:hidden flex items-center justify-between mt-1">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${p.inStock ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-600"}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${p.inStock ? "bg-green-500" : "bg-blue-400"}`}></span>
            {p.inStock ? "In Stock" : "Request Item"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {customerPrice !== undefined ? (
            <span className="text-sm font-semibold text-green-600">${customerPrice}</span>
          ) : p.price && parseFloat(p.price) > 0 ? (
            <span className="text-sm font-semibold text-accent">${p.price}</span>
          ) : p.price !== null && p.price !== undefined ? (
            <span className="text-xs text-amber-600 font-medium">Contact for pricing</span>
          ) : hasUser && userIsActive ? (
            <span className="text-xs text-amber-600 font-medium">Contact for pricing</span>
          ) : (
            <Link
              to="/register"
              onClick={() => trackEvent("signup_intent", {
                location: "catalog_price_mobile",
                product_id: p.id,
              })}
              className="text-xs text-accent hover:underline"
            >
              Sign up for pricing
            </Link>
          )}
          {canAddToCart && p.inStock && (
            <div className="flex flex-col items-end gap-1" onClick={e => e.preventDefault()}>
              <div className="flex items-center gap-1">
                <button aria-label={`Decrease quantity for ${p.name}`} onClick={e => { e.preventDefault(); e.stopPropagation(); onStepQty(p.id, -1); }} className="w-5 h-5 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm font-bold leading-none">−</button>
                <input
                  type="number"
                  aria-label={`${caseQuantity ? "Cases" : "Quantity"} for ${p.name}`}
                  min={1}
                  value={rawInput}
                  onChange={e => { onSetRaw(p.id, e.target.value); }}
                  onBlur={() => onCommitRaw(p.id)}
                  onFocus={e => e.target.select()}
                  onClick={e => e.stopPropagation()}
                  className="w-8 text-center text-xs border border-gray-300 rounded py-0.5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button aria-label={`Increase quantity for ${p.name}`} onClick={e => { e.preventDefault(); e.stopPropagation(); onStepQty(p.id, +1); }} className="w-5 h-5 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm font-bold leading-none">+</button>
              </div>
              {caseQuantity && <span className="text-[9px] text-amber-700">{caseQuantity} units/case</span>}
              <button
                onClick={(e) => onAddToCart(e, p.id)}
                disabled={addingToCart}
                aria-label={addedToCart ? `Added ${p.name} to cart` : `Add ${p.name} to cart`}
                className={`text-xs px-3 py-1 rounded-lg font-medium transition-all ${
                  addedToCart ? "bg-green-50 text-green-700 border border-green-200" : "bg-accent text-white hover:bg-accent/90"
                }`}
              >
                {addingToCart ? "..." : addedToCart ? "Added!" : "+ Cart"}
              </button>
            </div>
          )}
          {canAddToCart && !p.inStock && (
            <button
              onClick={(e) => onRequestSpecialOrder(e, p.id)}
              disabled={requestingProduct || productRequested}
              aria-label={productRequested ? `${p.name} request submitted` : `Request 2-day shipping for ${p.name}`}
              className={`text-xs px-2 py-1 rounded-lg font-medium transition-all ${
                productRequested ? "bg-green-50 text-green-700 border border-green-200" : "bg-orange-500 text-white hover:bg-orange-600"
              }`}
            >
              {requestingProduct ? "..." : productRequested ? "Requested!" : "Request Item"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export const ProductListCard = memo(ProductListCardInner);
